import { gradienteVertical } from "../../Graficos/gerador/tema";

/**
 * Ranking do Comex no formato de imagem da casa (modelo "barra-ranking" do
 * Gerador de Gráficos): maiores exportadores por estado, maiores compradores
 * por país, maiores URFs.
 *
 * O total da caixa é o do período filtrado inteiro, não a soma das barras
 * exibidas — é assim que o gráfico de referência mostra ("Total: 83,0
 * milhões/t" com só cinco estados na tela).
 */

const MESES_CURTOS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** Cores das barras, na ordem do ranking (amostradas dos gráficos publicados). */
const CORES_RANKING = [
  ["#3DAA73", "#1B5E38"],
  ["#9B7FD4", "#4B3A80"],
  ["#E34B4B", "#7A1F1F"],
  ["#F0A32E", "#8A5A10"],
  ["#3F8FE0", "#1B4C80"],
  ["#00C2C7", "#00595C"],
  ["#F0F000", "#7D8015"],
  ["#FF7AC8", "#8A3766"],
  ["#A0E050", "#4A7A20"],
  ["#8C8C8C", "#3D3D3D"],
];

export const DIMENSOES_RANKING = [
  { value: "uf", label: "Estado (UF)" },
  { value: "regiao", label: "Região" },
  { value: "pais", label: "País" },
  { value: "urf", label: "URF (porto/aduana)" },
];

/** "Maiores Exportadores", "Maiores Compradores"… conforme dimensão e fluxo. */
export function tituloDoRanking(dimensao, fluxo) {
  const importacao = fluxo === "Importação";
  if (dimensao === "uf" || dimensao === "regiao") return importacao ? "Maiores Importadores" : "Maiores Exportadores";
  if (dimensao === "urf") return importacao ? "Principais Portos de Entrada" : "Principais Portos de Saída";
  return importacao ? "Maiores Fornecedores" : "Maiores Compradores";
}

/** "Jan a Jul/2026", "2026" ou "2021 a 2026". */
export function textoPeriodo(anoInicio, anoFim, meses) {
  const anos = anoInicio === anoFim ? String(anoFim) : `${anoInicio} a ${anoFim}`;
  if (!meses?.length || meses.length === 12) return anos;
  const ordenados = [...meses].sort((a, b) => a - b);
  const seguidos = ordenados.every((m, i) => i === 0 || m === ordenados[i - 1] + 1);
  const nomes = ordenados.map((m) => MESES_CURTOS[m - 1]);
  const faixa = seguidos && ordenados.length > 1 ? `${nomes[0]} a ${nomes[nomes.length - 1]}` : nomes.join(", ");
  return `${faixa}/${anos}`;
}

/**
 * Valor e unidade de cada item conforme a métrica escolhida.
 * O valor médio por tonelada é FOB ÷ peso de cada item — não dá para somar
 * médias, então ele é recalculado a partir dos totais.
 */
function valoresDaMetrica(itens, metrica, moeda, unidadeQtd = "un") {
  const moedaTexto = moeda === "brl" ? "R$" : "US$";
  const cab = unidadeQtd === "cab";
  if (metrica === "quilograma_liquido") {
    const toneladas = itens.map((i) => (i.total_kg_liquido || 0) / 1000);
    const maior = Math.max(...toneladas, 0);
    return maior >= 1e6
      ? { valores: toneladas.map((t) => t / 1e6), unidade: "milhão/t", unidadeTotal: "milhões/t", decimais: 2 }
      : { valores: toneladas.map((t) => t / 1e3), unidade: "mil/t", unidadeTotal: "mil/t", decimais: 2 };
  }
  if (metrica === "quantidade_estatistica") {
    const q = itens.map((i) => i.total_quantidade || 0);
    const maior = Math.max(...q, 0);
    if (maior >= 1e6) {
      const u = cab ? "milhões de cab" : "milhões";
      return { valores: q.map((v) => v / 1e6), unidade: u, unidadeTotal: u, decimais: 2 };
    }
    if (cab && maior >= 1e3) {
      return { valores: q.map((v) => v / 1e3), unidade: "mil cab", unidadeTotal: "mil cab", decimais: 2 };
    }
    const u = cab ? "cab" : "unidades";
    return { valores: q, unidade: u, unidadeTotal: u, decimais: 0 };
  }
  if (metrica === "valor_medio_qtd") {
    return {
      valores: itens.map((i) => (i.total_quantidade ? i.total_fob_usd / i.total_quantidade : 0)),
      unidade: `${moedaTexto}/${unidadeQtd}`,
      unidadeTotal: `${moedaTexto}/${unidadeQtd}`,
      decimais: 2,
      semTotal: true,
    };
  }
  if (metrica === "valor_medio_ton") {
    return {
      valores: itens.map((i) => (i.total_kg_liquido ? (i.total_fob_usd / i.total_kg_liquido) * 1000 : 0)),
      unidade: `${moedaTexto}/t`,
      unidadeTotal: `${moedaTexto}/t`,
      decimais: 0,
      semTotal: true, // média de médias não existe: o "total" seria enganoso
    };
  }
  const fob = itens.map((i) => i.total_fob_usd || 0);
  const maior = Math.max(...fob, 0);
  return maior >= 1e9
    ? { valores: fob.map((v) => v / 1e9), unidade: `${moedaTexto} bilhões`, unidadeTotal: `${moedaTexto} bilhões`, decimais: 2 }
    : { valores: fob.map((v) => v / 1e6), unidade: `${moedaTexto} milhões`, unidadeTotal: `${moedaTexto} milhões`, decimais: 2 };
}

const formatar = (valor, decimais) =>
  Number(valor).toLocaleString("pt-BR", { minimumFractionDigits: decimais, maximumFractionDigits: decimais });

/**
 * @param itens  linhas do /comex-consulta/ranking, já somadas e ordenadas
 * @returns spec do Gerador (modelo barra-ranking) ou null se não há dados
 */
export function montarRankingComex(itens, opcoes = {}) {
  const {
    dimensao = "uf",
    fluxo = "Exportação",
    produto = "",
    metrica = "valor_fob_usd",
    moeda = "usd",
    unidadeQtd = "un",
    anoInicio,
    anoFim,
    meses = [],
    top = 5,
    mostrarTotal = true,
  } = opcoes;

  if (!itens?.length) return null;

  const exibidos = itens.slice(0, Math.max(1, top));
  const { valores, unidade, unidadeTotal, decimais, semTotal } = valoresDaMetrica(exibidos, metrica, moeda, unidadeQtd);
  // O total usa a lista inteira, com a mesma escala das barras.
  const todos = valoresDaMetrica(itens, metrica, moeda, unidadeQtd);
  const escalaIgual = todos.unidade === unidade;
  const total = escalaIgual ? todos.valores.reduce((a, v) => a + v, 0) : null;

  const linhaProduto = [produto, fluxo].filter(Boolean).join(" ");

  return {
    modelo: "barra-ranking",
    titulo: [
      { texto: `${linhaProduto} - Brasil `, cor: "#F0F000" },
      { texto: `(${unidade})`, cor: "#FFFFFF", tamanho: 9 },
    ],
    subtitulo: [
      { texto: `${tituloDoRanking(dimensao, fluxo)} - ${textoPeriodo(anoInicio, anoFim, meses)}`, cor: "#4EA6FF" },
    ],
    fonte: "Fonte: RURAL BUSINESS/ME",
    fundo: "#000000",
    fundoPlot: "nenhum",
    dados: {
      categorias: exibidos.map((i) => i.dimensao),
      series: [
        {
          nome: linhaProduto || "Ranking",
          cor: CORES_RANKING[0][0],
          corFim: CORES_RANKING[0][1],
          valores: valores.map((v) => Number(v.toFixed(decimais))),
        },
      ],
    },
    legenda: { visivel: false },
    valores: { decimais, prefixo: "", sufixo: "", exibirEm: "todos" },
    rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000", opacidade: 1 },
    extras: {
      formato: "chato",
      caixaValor: true,
      coresPorItem: CORES_RANKING.map(([cor, corFim]) => gradienteVertical(cor, corFim)),
      caixas:
        mostrarTotal && !semTotal && total !== null
          ? [
              {
                linhas: [`Total: ${formatar(total, 1)}`, unidadeTotal],
                em: { fx: 0.62, fy: 0.62 },
                corFundo: "#1A1A1A",
                corBorda: "#FFFFFF",
                corTexto: "#FFFFFF",
                opacidade: 1,
                tamanho: 11,
              },
            ]
          : [],
    },
  };
}
